import React from 'react';
import PropTypes from 'prop-types';
import {
    WeezHeader,
    Button,
    Tab,
    SplitContainer,
    Message,
    Card,
    RadioLabel,
    TranslateDropdown,
    Input,
    OptionalSection,
    TextAreaLabel,
    ToggleLabel,
    Tooltip,
    TooltipHelp,
    Icon,
    ToastContext
} from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Style from './editCrossSelling.module.css';
import CrossSellingModel from '../../../models/crossSellingModel';
import TaxModel from '../../../models/taxModel';
import SelectLabelIcon from '../selectLabelIcon';
import VariantList from './variantList';
import EventModel from '../../../models/eventModel';
import PutForSale from './putForSale';
import PutForSaleModel from '../../../models/productSaleModel';
import VariantModel from '../../../models/variantModel';
import SelectTaxes from '../../../components/tax/selectTaxes';
import LoadingCard from '../../../components/loadingCard';
import { convertMenhirToPyvarPipeTranslate } from '../../../components/utils/pipeTranslateConvert';
import { useForm } from '@weezevent/weezjs-core';

import { getProductsLanguages, getDefaultLanguage, getLanguagesTranslateDropdown, removeDefaultLanguage } from './list/translationUtils';

export default function EditCrossSelling({ organization, productId, page = 0 }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    let [forSaleArray, setForSaleArray] = React.useState([]);
    let [salesToDelete, setSalesToDelete] = React.useState([]);
    const [isSaveButtonLoading, setIsSaveButtonLoading] = React.useState(false);
    // Note for later: replace it by React.useForm
    const [product, setProduct] = React.useState({
        id: productId ?? null,
        organization: organization.id,
        name: '',
        description: '',
        disabled: false,
        for_buyer: false,
        choose_none_option: false,
        display_quantity: false,
        display_out_of_stock: false,
        display_order: 0,
        category: null,
        tva: null,
        translations: {
            name: {},
            description: {}
        }
    });

    const productsLanguages = React.useMemo(() => getProductsLanguages(organization), [organization]);
    const defaultLanguage = React.useMemo(() => getDefaultLanguage(organization, productsLanguages), [organization, productsLanguages]);
    const languagesTranslateDropdown = React.useMemo(() => getLanguagesTranslateDropdown(productsLanguages), [productsLanguages]);

    const [productLanguage, setProductLanguage] = React.useState(defaultLanguage);
    const [descLanguage, setDescLanguage] = React.useState(defaultLanguage);
    const [activeTab, setActiveTab] = React.useState(Number(page));
    const [submitting, setSubmitting] = React.useState(false);
    const [tax, setTax] = React.useState();
    const [taxes, loadingTaxes] = TaxModel.useApiModel({});
    const [variants, setVariants] = React.useState([]);
    const [querySearch, setQuerySearch] = React.useState('');

    const defaultclickErrors = { price: false, name: false };

    const defaultVariant = {
        quota: null,
        name: '',
        id: null,
        price: 0,
        ordering: variants ? variants.length : 0,
        disabled: false,
        reference: '',
        translations: {
            name: {}
        }
    };

    const [modalForm, setForm, setFormObject] = useForm({
        ...defaultVariant
    });

    const resetmodal = React.useCallback(
        (variants = null) => {
            let _defaultVariant = { ...defaultVariant };
            if (variants) {
                _defaultVariant.ordering = variants.length;
            }

            setFormObject({
                ..._defaultVariant
            });
            setErrorsClick(defaultclickErrors);
        },
        [setFormObject, defaultVariant, defaultclickErrors]
    );

    const getPipeTranslate = React.useCallback(
        string => {
            let translations = {};

            if (string.includes('|')) {
                string.split('|').forEach(item => {
                    let pipe = convertMenhirToPyvarPipeTranslate(item);
                    let regLang = pipe.slice(1, 6).toLowerCase();
                    pipe = pipe.slice(7, pipe.length);
                    if (productsLanguages?.find(l => regLang === l)) translations[regLang] = pipe;
                });
            }
            return translations;
        },
        [productsLanguages]
    );

    const initTranslations = React.useCallback(
        (obj, lang, fields) => {
            if (!obj.translations) obj.translations = {};
            fields?.forEach(field => {
                const pipeTranslate = getPipeTranslate(obj[field]);
                obj.translations[field] = { ...pipeTranslate, ...obj.translations[field] };
                //use name if no translations for default language
                if (obj.translations[field][lang]) obj[field] = obj.translations[field][lang];
                if (!obj.translations[field][lang]) obj.translations[field][lang] = obj[field];
                //we filter the languages keys that arent in organization
                Object.keys(obj.translations[field]).forEach(langKey => {
                    if (!productsLanguages.some(ol => ol === langKey)) obj.translations[field][langKey] = '';
                });
            });
            return obj;
        },
        [productsLanguages]
    );

    const [, loadingProduct, sync] = CrossSellingModel.useApiModel(
        {
            organizationId: organization.id,
            id: productId,
            launch: Boolean(productId),
            cache: false,
            allow_cache: false,
            onSuccess: response => {
                let obj = response.object;
                //we init the translations in case we don't have any
                obj = initTranslations(obj, defaultLanguage, ['name', 'description']);
                setProduct(obj);
                setTax(obj.tva);
            }
        },
        [productId, organization, defaultLanguage]
    );

    const [, loaderVariant, syncVariants] = VariantModel.useApiModel(
        {
            organizationId: organization.id,
            productId: productId,
            launch: Boolean(productId),
            query: {
                include: ['transaction_total_count']
            },
            onSuccess: response => {
                let objs = response;
                //we init the translations in case we don't have any
                objs = objs.map(obj => initTranslations(obj, defaultLanguage, ['name']));
                setVariants(objs);
                resetmodal(objs);
            }
        },
        [productId, organization, defaultLanguage]
    );

    const [events, loadingEvents] = EventModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            allow_cache: false,
            query: {
                search: querySearch,
                limit: 50,
                offset: 0,
                publication_status__in: ['draft', 'published'],
                time_status: ['upcoming', 'ongoing']
            }
        },
        [organization, querySearch]
    );

    let [errorsClick, setErrorsClick] = React.useState(
        productId
            ? {
                  tva: true,
                  name: true,
                  category: true
              }
            : {
                  tva: false,
                  name: false,
                  category: false
              }
    );

    const panes = React.useMemo(() => {
        const existingProduct = Boolean(productId);
        return [
            {
                menuItem: t('ticket.cross-selling.add.pan.article'),
                menuIcon: 'package'
            },
            {
                menuItem: t('ticket.cross-selling.add.pan.variant'),
                menuIcon: 'cart',
                disabled: !existingProduct
            },
            {
                menuItem: t('ticket.cross-selling.add.pan.toSell'),
                menuIcon: 'user',
                disabled: !existingProduct || variants?.length === 0
            }
        ];
    }, [productId, variants]);

    const setTranslate = React.useCallback(string => {
        let reg = new RegExp(/\[([a-z]{2}[-|_]?[A-Z]{0,2})\](.*)/);

        let translations = { 'fr-FR': string, 'en-GB': '', 'it-IT': '', 'es-ES': '', 'en-US': '' };

        if (string.includes('|')) {
            string.split('|').forEach(item => {
                let matches = item.match(reg);
                if (matches?.length > 2) {
                    let locale = matches[1].replace('_', '-');
                    translations[locale] = matches[2];
                }
            });
        }
        return translations;
    }, []);

    // Triggered while creating a Product
    const onSave = React.useCallback(
        value => {
            setSubmitting(true);
            return new CrossSellingModel(value)
                .save({
                    organizationId: organization.id
                })
                .then(res => {
                    toast.success(t('ticket.cross-selling.add.productAdded'));
                    history.push(`/ticket/O${organization.id}/cross-selling/edit/${res.response.id}/page/` + 1);
                })
                .finally(() => {
                    setSubmitting(false);
                    setActiveTab(1);
                    setProductLanguage(defaultLanguage);
                    setDescLanguage(defaultLanguage);
                    sync();
                });
        },
        [history, organization, sync, defaultLanguage]
    );

    const onEdit = React.useCallback(
        value => {
            setSubmitting(true);
            return new CrossSellingModel({ id: value.id, ...value })
                .save({
                    organizationId: organization.id
                })
                .then(() => {
                    toast.success(t('ticket.cross-selling.add.productEdited'));
                })
                .catch(e => {
                    toast.error(t(`ticket.cross-selling.add.productEditError`));
                })
                .finally(() => {
                    setSubmitting(false);
                    setProductLanguage(defaultLanguage);
                    setDescLanguage(defaultLanguage);
                    sync();
                });
        },
        [organization, sync, defaultLanguage]
    );

    const errorName = React.useCallback(() => {
        return !product.name && errorsClick.name ? true : false;
    }, [product, errorsClick]);

    const disableSaveButton = React.useMemo(() => {
        return Boolean(tax === undefined || !product.category || !product.name);
    }, [tax, product, errorName]);

    const postMEV = React.useCallback(() => {
        setIsSaveButtonLoading(true);
        let arrayPromiseSaleDelete = [];
        if (salesToDelete?.length) {
            arrayPromiseSaleDelete = salesToDelete.map(obj => {
                return new PutForSaleModel(obj).delete({
                    organizationId: organization.id,
                    productId: productId
                });
            });
        }

        let _new = [...forSaleArray];
        let arrayPromiseSaleCreate = forSaleArray.map(r => {
            return new PutForSaleModel(r).save({
                organizationId: organization.id,
                productId: productId
            });
        });
        Promise.all([...arrayPromiseSaleDelete, ...arrayPromiseSaleCreate]).then(arrayResponse => {
            for (let result of arrayResponse) {
                if (result.status === 201) {
                    let f = forSaleArray.find(l => result.response.rate === l.rate);
                    if (!f.id) {
                        let i = forSaleArray.findIndex(l => result.response.rate === l.rate);

                        _new[i].id = result.response.id;
                    }
                }
            }
            setSalesToDelete([]);
            setForSaleArray(_new);
            toast.success(t('ticket.cross-selling.putForSale.edited'));
            setIsSaveButtonLoading(false);
        });
    }, [salesToDelete, forSaleArray, organization, productId]);

    const productCanBeVisible = React.useMemo(() => {
        return Boolean(product.variants?.length && variants?.some(vr => !vr.disabled));
    }, [product, variants]);

    const onChangeCategory = React.useCallback(
        value => {
            setProduct({ ...product, category: value, ...(value === 1 && { for_buyer: false }) });
            if (value === 1) {
                setTax(0);
            }
        },
        [product]
    );

    return (
        <>
            <WeezHeader
                title={productId ? t('ticket.cross-selling.add.editTitle') : t('ticket.cross-selling.add.title')}
                backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/cross-selling`) }}
                rightComponent={[
                    <Button
                        primary
                        key="rate"
                        disabled={disableSaveButton}
                        isLoading={isSaveButtonLoading}
                        onClick={() => {
                            if (activeTab === 2) {
                                postMEV();
                                return;
                            }
                            setErrorsClick({
                                tva: true,
                                name: true,
                                category: true
                            });

                            if (product.id || productId) {
                                onEdit(
                                    {
                                        id: product.id || productId,
                                        organization: organization.id,
                                        translations: removeDefaultLanguage(product?.translations, defaultLanguage),
                                        name: product?.name,
                                        description: product?.description,
                                        disabled: product?.disabled,
                                        for_buyer: product?.for_buyer,
                                        choose_none_option: product?.choose_none_option,
                                        display_quantity: product?.display_quantity,
                                        display_out_of_stock: product?.display_out_of_stock,
                                        display_order: 0,
                                        category: product?.category,
                                        tva: tax
                                    },
                                    product.id || productId
                                );
                            } else {
                                onSave({
                                    organization: organization.id,
                                    translations: removeDefaultLanguage(product?.translations, defaultLanguage),
                                    name: product?.name,
                                    description: product?.description,
                                    disabled: product?.disabled,
                                    for_buyer: product?.for_buyer,
                                    choose_none_option: product?.choose_none_option,
                                    display_quantity: product?.display_quantity,
                                    display_out_of_stock: product?.display_out_of_stock,
                                    display_order: 0,
                                    category: product?.category,
                                    tva: tax
                                });
                            }
                        }}
                        label={t('ticket.common.action.save')}
                    />
                ]}
            />

            <SplitContainer
                additional={
                    <div>
                        <Message mini title={t('ticket.common.tips')}>
                            {t(`ticket.cross-selling.info${activeTab + 1}`)}
                        </Message>
                    </div>
                }
            >
                <Tab
                    panes={panes}
                    onTabSelected={(tab, idx) => {
                        setActiveTab(idx);
                        setProductLanguage(defaultLanguage);
                        setDescLanguage(defaultLanguage);
                    }}
                    activeTab={activeTab}
                >
                    {activeTab === 0 && (
                        <Card>
                            <LoadingCard loading={(Boolean(productId) && loadingProduct) || loadingTaxes || submitting}>
                                <Card.Header>
                                    <Card.Title title={t('ticket.cross-selling.add.information')} />
                                    <Card.SubTitle title={t('ticket.cross-selling.add.addProductSub')} />
                                </Card.Header>
                                <Card.Content>
                                    <div>{t('ticket.cross-selling.add.visibility')}</div>
                                    <div className={Style['tooltip-no-left']}>
                                        <Tooltip triggerOnHover={!productCanBeVisible} visible={!productCanBeVisible} title={t('ticket.cross-selling.visibility_tooltip')}>
                                            <RadioLabel
                                                disabled={!productCanBeVisible}
                                                currentPage={product?.disabled !== null ? !product?.disabled : false}
                                                checked={product?.disabled !== null ? !product?.disabled : false}
                                                onClick={() => {
                                                    setProduct({ ...product, disabled: false });
                                                }}
                                                name="visible-product"
                                                value="male3"
                                                inline
                                                bordered
                                            >
                                                {t('ticket.cross-selling.add.visibleOnline')} {<Icon name="eye" />}
                                            </RadioLabel>
                                        </Tooltip>
                                        <RadioLabel
                                            checked={product?.disabled !== null ? product?.disabled : false}
                                            currentPage={product?.disabled !== null ? product?.disabled : false}
                                            onClick={() => {
                                                setProduct({ ...product, disabled: true });
                                            }}
                                            name="visible-product"
                                            value="female3"
                                            inline
                                            bordered
                                        >
                                            {t('ticket.common.action.hide-online')} {<Icon name="eye-slash" />}
                                        </RadioLabel>
                                    </div>
                                    <div className={Style['add-cross-selling-name']}>
                                        <div>{t('ticket.cross-selling.add.name')}</div>
                                        <TranslateDropdown
                                            defaultValue={defaultLanguage}
                                            languages={languagesTranslateDropdown}
                                            onChange={e => setProductLanguage(e)}
                                            selected={Object.entries(product.translations.name)
                                                .map(([k, v]) => (v ? k : undefined))
                                                .filter(e => e)}
                                        />
                                    </div>
                                    <Input
                                        value={product.translations.name[productLanguage] || ''}
                                        errors={errorName()}
                                        onBlur={e => {
                                            setErrorsClick({ ...errorsClick, name: true });
                                        }}
                                        onClick={e => {
                                            setErrorsClick({ ...errorsClick, name: false });
                                        }}
                                        onChange={event => {
                                            let ret = { ...product };
                                            ret.translations.name[productLanguage] = event.target.value || '';
                                            if (productLanguage === defaultLanguage) ret.name = event.target.value;
                                            setProduct(ret);
                                        }}
                                    />
                                    <div className={Style['add-cross-selling-description']}>
                                        <OptionalSection optionalText={t('ticket.common.labelOptional')} label={t('ticket.cross-selling.add.addDescription')}>
                                            <div className={Style['add-cross-selling-description-containt']}>
                                                <div>{t('ticket.cross-selling.add.description')}</div>
                                                <TranslateDropdown
                                                    defaultValue={defaultLanguage}
                                                    languages={languagesTranslateDropdown}
                                                    onChange={e => setDescLanguage(e)}
                                                    selected={Object.entries(product.translations.description)
                                                        .map(([k, v]) => (v ? k : undefined))
                                                        .filter(e => e)}
                                                />
                                            </div>

                                            <TextAreaLabel
                                                value={product.translations.description[descLanguage] || ''}
                                                onChange={event => {
                                                    let ret = { ...product };
                                                    ret.translations.description[descLanguage] = event.target.value || '';
                                                    if (descLanguage === defaultLanguage) ret.description = event.target.value;
                                                    setProduct(ret);
                                                }}
                                                maxlength={450}
                                                showlength={450}
                                                placeholder={t('ticket.cross-selling.add.textAreaPlaceholder')}
                                            />
                                        </OptionalSection>
                                    </div>
                                    <div className={Style['content-input-select-category']}>
                                        <SelectLabelIcon
                                            label={t('ticket.cross-selling.add.category')}
                                            placeholder={t('ticket.common.action.select')}
                                            errors={!product.category && errorsClick.category}
                                            value={product?.category}
                                            onChange={onChangeCategory}
                                            options={[
                                                { name: 'Service', icon: 'service', value: 3 },
                                                { name: 'Produits', icon: 'shirt', value: 2 },
                                                { name: 'Cashless', icon: 'credit-card', value: 1 }
                                            ]}
                                        />
                                    </div>
                                    {product.category === 1 && (
                                        <Message type="default" title={t('ticket.cross-selling.cashless-message-title')}>
                                            {t('ticket.cross-selling.cashless-message-subtitle')}
                                        </Message>
                                    )}
                                    <div>
                                        <SelectTaxes
                                            taxes={taxes}
                                            label={t('ticket.cross-selling.add.taxeLabel')}
                                            errors={tax === undefined && errorsClick.tva}
                                            onChange={e => {
                                                setTax(e);
                                            }}
                                            value={tax}
                                            disabled={product.category === 1}
                                        />
                                    </div>
                                    <OptionalSection label={t('ticket.cross-selling.add.showOption')}>
                                        <ToggleLabel
                                            disabled={product.category === 1 || product.transaction_total_count > 0}
                                            label={
                                                <>
                                                    {t('ticket.cross-selling.add.for_buyer')}
                                                    <TooltipHelp title={t('ticket.cross-selling.add.for_buyer_tooltips')} mini={false} position={'top'} />
                                                </>
                                            }
                                            inlinePosition="left"
                                            onChange={() => {
                                                setProduct(currentstate => {
                                                    let newState = { ...currentstate };
                                                    newState.for_buyer = !currentstate.for_buyer;
                                                    return newState;
                                                });
                                            }}
                                            checked={product?.for_buyer}
                                        />
                                        <ToggleLabel
                                            label={t('ticket.cross-selling.add.choose_none_option')}
                                            inlinePosition="left"
                                            onChange={() =>
                                                setProduct(currentstate => {
                                                    let newState = { ...currentstate };
                                                    newState.choose_none_option = !currentstate.choose_none_option;
                                                    return newState;
                                                })
                                            }
                                            checked={product?.choose_none_option}
                                        />
                                        <ToggleLabel
                                            label={t('ticket.cross-selling.add.display_quantity')}
                                            inlinePosition="left"
                                            onChange={() =>
                                                setProduct(currentstate => {
                                                    let newState = { ...currentstate };
                                                    newState.display_quantity = !currentstate.display_quantity;
                                                    return newState;
                                                })
                                            }
                                            checked={product?.display_quantity}
                                        />
                                        <ToggleLabel
                                            label={t('ticket.cross-selling.add.display_out_of_stock')}
                                            inlinePosition="left"
                                            onChange={() =>
                                                setProduct(currentstate => {
                                                    let newState = { ...currentstate };
                                                    newState.display_out_of_stock = !currentstate.display_out_of_stock;
                                                    return newState;
                                                })
                                            }
                                            checked={product?.display_out_of_stock}
                                        />
                                    </OptionalSection>
                                </Card.Content>
                            </LoadingCard>
                        </Card>
                    )}
                    {activeTab === 1 && (
                        <div>
                            <VariantList
                                syncProduct={sync}
                                syncVariants={syncVariants}
                                loadingVariants={loaderVariant}
                                variants={variants}
                                setVariants={setVariants}
                                organization={organization}
                                product={product}
                                productId={productId || product.id}
                                productCategory={product?.category}
                                modalForm={modalForm}
                                setForm={setForm}
                                setFormObject={setFormObject}
                                resetmodal={resetmodal}
                                defaultclickErrors={defaultclickErrors}
                            />
                        </div>
                    )}
                    {activeTab === 2 && (
                        <>
                            <PutForSale
                                events={events}
                                querySearch={querySearch}
                                setQuerySearch={setQuerySearch}
                                loadingEvents={loadingEvents}
                                setForSaleArray={setForSaleArray}
                                forSaleArray={forSaleArray}
                                salesToDelete={salesToDelete}
                                onSalesToDelete={setSalesToDelete}
                                category={product.category}
                                organization={organization}
                                productId={parseInt(productId, 10) || parseInt(product.id, 10)}
                                setTranslate={setTranslate}
                            />
                        </>
                    )}
                </Tab>
            </SplitContainer>
        </>
    );
}

EditCrossSelling.propTypes = {
    organization: PropTypes.object.isRequired
};
